import moment from 'moment';
import { Module } from 'vuex';

import { rolesRepository } from '@/components/users/repositories/roles.repository';
import { Contact } from '@/modules/contacts/shared/domain/contact';
import {
    transformContactToCreate,
    transformContactToUpdate,
    transformContactToUpdateAndCreateUser,
    transformRolToCreate,
    transformRolToUpdate,
} from '@/modules/contacts/shared/domain/contact-form-parser';
import {
    ContactFormInterface,
    DeleteContactRequest,
} from '@/modules/contacts/shared/domain/contact-form.interface';
import { ContactSelection } from '@/modules/contacts/shared/domain/contact-selection';
import { ContactType } from '@/modules/contacts/shared/domain/contact-type';
import { ContactView } from '@/modules/contacts/shared/domain/contact.view';
import { CustomerContact } from '@/modules/contacts/shared/domain/customer-contact';
import { Resource } from '@/modules/contacts/shared/domain/resource.interface';
import { SgcPrivilege } from '@/modules/contacts/shared/domain/sgc-privilege';
import { TicketRequest } from '@/modules/contacts/shared/domain/tickets-request.interface';
import { contactsRepository } from '@/modules/contacts/shared/repositories/contacts.repository';
import { ContactsState } from '@/modules/contacts/shared/store/contact.state';
import {
    CHECK_IF_CONTACT_EXISTS,
    CONTACTS_CREATE_CONTACT,
    CONTACTS_CREATE_ROLE,
    CONTACTS_CREATE_USER,
    CONTACTS_FETCH_VIEW,
    CONTACTS_UPDATE_CONTACT,
    CONTACTS_UPDATE_ROLE,
    ContactsActions,
} from '@/modules/contacts/shared/store/contacts.actions';
import {
    CONTACT_GET_CONTACT_PRIVILEGES,
    CONTACT_GET_CONTACT_RESOURCES,
    CONTACT_GET_CONTACT_SCALABILITY_LEVELS,
    CONTACT_GET_CONTACT_TO_BE_UPDATED,
    CONTACT_GET_CONTACT_TYPES,
    CONTACT_GET_FORM_VIEW,
    CONTACT_GET_LAST_ROUTE,
    ContactsGetters,
} from '@/modules/contacts/shared/store/contacts.getters';
import {
    CONTACT_SET_CONTACT_TO_UPDATE,
    CONTACT_SET_CUSTOMER,
    CONTACT_SET_FORM_VIEW,
    CONTACT_SET_LAST_ROUTE,
    CONTACT_SET_ROL_CREATED,
    CONTACTS_SET_VIEW,
    ContactsMutations,
} from '@/modules/contacts/shared/store/contacts.mutations';
import { Customer } from '@/modules/customers/domain/customer';
import customerRepository from '@/modules/customers/repositories/customers.repository';
import {
    ContactManagementType,
    ContactValidation,
    ResultStatus,
    Tickets,
    TicketType,
} from '@/modules/shared/shared/domain/constants/constants';
import { HttpStatus } from '@/modules/shared/shared/domain/constants/http-status';
import { MONTH_SHORT_NAMES } from '@/modules/shared/shared/domain/constants/month-names';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { UserContactTicket } from '@/modules/tickets/shared/domain/user-contact-ticket';
import ticketsRepository from '@/modules/tickets/shared/repositories/tickets.repository';
import { Privilege } from '@/modules/users/domain/privilege.interface';
import { UserHistory } from '@/modules/users/domain/user-history';

import { ScalabilityLevel } from '../domain/scalability-level';

const contacts: Module<ContactsState, any> = {
    namespaced: true,
    state: {
        contactView: {
            customerRoles: [],
            statuses: [],
            privileges: [],
            resources: [],
            contactTypes: [],
            scalabilityLevels: [],
        },
        roleCreated: {
            customerRole: {
                id: -1,
            },
        },
        contactToBeUpdated: undefined,
        formView: '',
        customer: undefined,
        lastRoute: '',
        directoryContacts: [],
        selectedContact: undefined,
    },
    getters: {
        [CONTACT_GET_CONTACT_PRIVILEGES](state): Privilege[] {
            return state.contactView.privileges;
        },
        [CONTACT_GET_CONTACT_RESOURCES](state): Resource[] {
            return state.contactView.resources.filter(i => i.sgcName);
        },
        [CONTACT_GET_CONTACT_TYPES](state): ContactType[] {
            return state.contactView.contactTypes;
        },
        [CONTACT_GET_CONTACT_SCALABILITY_LEVELS](state): ScalabilityLevel[] {
            return state.contactView.scalabilityLevels;
        },
        [CONTACT_GET_CONTACT_TO_BE_UPDATED](state): ContactFormInterface {
            return state.contactToBeUpdated!;
        },
        [CONTACT_GET_FORM_VIEW](state): string {
            return state.formView;
        },
        [ContactsGetters.GET_AVAILABLE_RESOURCES](state, getters): Resource[] {
            const availableResources = getters[CONTACT_GET_CONTACT_RESOURCES];
            return (
                availableResources.filter(i =>
                    state.selectedContact!.sgcResources!.find(j => j.privilege.name === i.sgcName),
                ) || []
            );
        },
        [CONTACT_GET_LAST_ROUTE](state): string {
            return state.lastRoute;
        },
        [ContactsGetters.GET_CURRENT_SELECTED_CONTACT_RESOURCES](state): string {
            if (state.selectedContact!.sgcResources && state.selectedContact!.sgcResources.length) {
                return state
                    .selectedContact!.sgcResources.map(i => {
                        if (i.privilege.name === 'Remote Hands') return 'Manos remotas';
                        if (i.privilege.name == 'Solicitud de Permisos de Trabajo (PDT)')
                            return 'Solicitud de Permisos de Acceso';
                        return i.privilege.name;
                    })
                    .join(', ');
            }
            return 'El contacto no tiene privilegios asociados';
        },
        [ContactsGetters.GET_DIRECTORY](state): CustomerContact[] {
            return state.directoryContacts;
        },
        [ContactsGetters.GET_CONTACT_TICKETS](state): UserContactTicket[] {
            let months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
            const currentMonth = moment().month();
            const currentYear = moment().year();
            const tickets = state.selectedContact!.tickets || [];

            months = months.slice(currentMonth + 1).concat(months.slice(0, currentMonth + 1));

            return months.map(month => {
                const tm = tickets.find(r => r.monthNumber === month + 1);
                return {
                    monthName: MONTH_SHORT_NAMES[month],
                    total: tm ? tm.total : 0,
                    monthNumber: month + 1,
                    year: month > currentMonth ? currentYear - 1 : currentYear,
                };
            });
        },

        [ContactsGetters.GET_CURRENT_SELECTION](state): ContactSelection {
            return state.selectedContact!;
        },
    },
    mutations: {
        [CONTACTS_SET_VIEW](state, contactView: ContactView): void {
            state.contactView = contactView;
        },
        [CONTACT_SET_ROL_CREATED](state, roleCreated: { customerRole: { id: number } }): void {
            state.roleCreated = roleCreated;
        },
        [CONTACT_SET_CONTACT_TO_UPDATE](state, contact: ContactFormInterface): void {
            state.contactToBeUpdated = contact;
        },
        [CONTACT_SET_FORM_VIEW](state, formView: string): void {
            state.formView = formView;
        },
        [CONTACT_SET_CUSTOMER](state, customer: Customer): void {
            state.customer = customer;
        },
        [CONTACT_SET_LAST_ROUTE](state, route: string): void {
            state.lastRoute = route;
        },
        [ContactsMutations.SET_DIRECTORY](state, contacts: CustomerContact[]) {
            state.directoryContacts = contacts;
        },
        [ContactsMutations.SET_SELECTED_CONTACT_DETAILS](state, contact: CustomerContact) {
            state.selectedContact = {
                ...contact,
                contactType: {
                    id: contact.contactTypeId,
                    name: contact.contactTypeName,
                },
                scalabilityLevel: {
                    id: contact.scalabilityLevelId,
                    name: contact.scalabilityLevelName,
                },
                userStatus: {
                    id: -1,
                    name: '',
                },
                sgcResources: [],
            };
        },
        [ContactsMutations.SET_SELECTED_CONTACT_USER_DATA](state, contact: UserHistory) {
            if (contact.users) {
                state.selectedContact = {
                    ...state.selectedContact!,
                    userId: contact.users.id,
                    userStatus: {
                        id: contact.users.status.id,
                        name: contact.users.status.name,
                    },
                    contactRoles: contact.users.customerRole,
                    tickets: contact.tickets || [],
                };
            }
        },
        [ContactsMutations.SET_SELECTED_CONTACT_PRIVILEGES](state, privileges: SgcPrivilege[]) {
            state.selectedContact!.sgcResources = privileges;
        },
    },
    actions: {
        async [CONTACTS_FETCH_VIEW]({ commit }, customerId: number): Promise<boolean> {
            try {
                const data = await rolesRepository.getView(customerId);
                commit(CONTACTS_SET_VIEW, data);
                return true;
            } catch (e) {
                return false;
            }
        },

        async [CHECK_IF_CONTACT_EXISTS](
            { commit },
            payload: {
                id: number;
                identification: string;
                customer: { id: number };
                email: string;
            },
        ): Promise<any> {
            try {
                return (await contactsRepository.checkIfContactExists(payload)) as any;
            } catch (e) {
                if (e && e.response && e.response.data) {
                    e = e.response.data;

                    if (e.status === HttpStatus.NOT_FOUND_EXCEPTION) {
                        return {
                            status: e.status,
                            message: e.message,
                            hint: ContactValidation.CONTACT_NOT_FOUND,
                        };
                    } else if (e.status === HttpStatus.BAD_REQUEST_EXCEPTION) {
                        return e;
                    }
                }
                return {
                    status: HttpStatus.BAD_REQUEST_EXCEPTION,
                    message: 'Ha ocurrido un error inesperado, vuelva a intentarlo en otro momento',
                    hint: ContactValidation.UNDEFINED_ERROR,
                };
            }
        },

        async [CONTACTS_CREATE_ROLE](
            { commit, state, dispatch },
            params: { selectedResources: Resource[] },
        ) {
            let privileges = state.contactView.privileges;

            privileges = privileges.filter(
                i =>
                    params.selectedResources &&
                    params.selectedResources.find(j => i.resource.id == j.id),
            );

            let sPrivileges = privileges.map(i => ({ privilege: i.id }));
            const roleToBeCreated = transformRolToCreate(state.customer!.id, sPrivileges);

            const data = (await rolesRepository.createRol(roleToBeCreated)).data;
            commit(CONTACT_SET_ROL_CREATED, data);
            dispatch(CONTACTS_FETCH_VIEW, state.customer!.id);
        },

        async [CONTACTS_CREATE_CONTACT](
            { commit, state, dispatch },
            params: {
                contact: ContactFormInterface;
                userContact: Contact;
                selectedResources: Resource[];
            },
        ): Promise<{ status: string; message: string }> {
            try {
                await dispatch(CONTACTS_CREATE_ROLE, {
                    selectedResources: params.selectedResources,
                });

                const customer: Customer = state.customer!;

                const contact = transformContactToCreate(
                    params.contact,
                    customer,
                    state.contactView.statuses,
                    state.roleCreated,
                );

                const ticket: TicketRequest = {
                    description: `El contacto ${params.userContact.name} ${
                        params.userContact.lastName
                    } del cliente ${params.userContact.customer.name} creó el contacto ${
                        params.contact.name
                    } ${params.contact.lastName}, ${params.contact.nationality +
                        params.contact.ci} para el cliente ${
                        customer.name
                    } en el Portal de Clientes Daycohost`,
                    ticketType: {
                        id: Tickets.REQUIREMENT.id,
                        name: TicketType.CONTACT,
                    },
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: {
                            id: customer.category ? customer.category!.id : -1,
                        },
                        contact: {
                            id: params.userContact.id,
                            name: params.userContact.name,
                            lastName: params.userContact.lastName,
                            identification: params.userContact.ci!,
                            phone: params.userContact.phone!,
                            email: params.userContact.email,
                        },
                    },
                    contact: contact,
                };
                await contactsRepository.createContact(ticket);
                return {
                    status: ResultStatus.SUCCESS,
                    message: `El contacto ${params.contact.name} ${params.contact.lastName} ha sido creado`,
                };
            } catch (e) {
                return {
                    status: ResultStatus.ERROR,
                    message: 'Ha ocurrido un error inesperado, vuelva a intentarlo en otro momento',
                };
            }
        },

        async [CONTACTS_UPDATE_ROLE](
            { commit, state, dispatch },
            params: { selectedResources: Resource[]; currentRole: {} },
        ) {
            let privileges = state.contactView.privileges;

            privileges = privileges.filter(
                i =>
                    params.selectedResources &&
                    params.selectedResources.find(j => i.resource.id == j.id),
            );

            let sPrivileges = privileges.map(i => ({ privilege: { id: i.id } }));
            const roleToBeUpdated = transformRolToUpdate(params.currentRole, sPrivileges);

            const data = (await rolesRepository.updateRol(roleToBeUpdated)).data;
            commit(CONTACT_SET_ROL_CREATED, data);
        },

        async [CONTACTS_UPDATE_CONTACT](
            { commit, state, dispatch },
            params: {
                contact: ContactFormInterface;
                userContact: Contact;
                selectedResources: Resource[];
                withUser: boolean;
            },
        ) {
            try {
                const customer: Customer = state.customer!;
                const contact = transformContactToUpdate(params.contact, customer);

                if (params.withUser) {
                    await dispatch(CONTACTS_UPDATE_ROLE, {
                        selectedResources: params.selectedResources,
                        currentRole: (<any>params.contact.contactRoles).role,
                    });
                } else {
                    delete contact.contactRoles;
                    delete contact.userId;

                    contact.contactPrivileges = params.selectedResources.map(i => {
                        return {
                            privilege: {
                                name: i.sgcName,
                            },
                        };
                    });
                }

                const ticket: TicketRequest = {
                    description: `El contacto ${params.userContact.name} ${params.userContact.lastName} del cliente ${params.userContact.customer.name} modificó el contacto ${params.contact.name} ${params.contact.lastName}, ${params.contact.email} del cliente ${customer.name} en el Portal de Clientes Daycohost. `,
                    ticketType: {
                        id: Tickets.REQUIREMENT.id,
                        name: TicketType.CONTACT,
                    },
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: {
                            id: customer.category ? customer.category!.id : -1,
                        },
                        contact: {
                            id: params.userContact.id,
                            name: params.userContact.name,
                            lastName: params.userContact.lastName,
                            identification: params.userContact.ci!,
                            phone: params.userContact.phone!,
                            email: params.userContact.email,
                        },
                    },
                    contact: contact,
                };

                await contactsRepository.updateContact(JSON.parse(JSON.stringify(ticket)));

                return {
                    status: ResultStatus.SUCCESS,
                    message: `El contacto ${params.contact.name} ${params.contact.lastName} ha sido actualizado`,
                };
            } catch (e) {
                return {
                    status: ResultStatus.ERROR,
                    message: 'Ha ocurrido un error inesperado, vuelva a intentarlo en otro momento',
                };
            }
        },
        async [CONTACTS_CREATE_USER](
            { commit, state, dispatch },
            params: {
                contact: ContactFormInterface;
                userContact: Contact;
                selectedResources: Resource[];
            },
        ) {
            try {
                await dispatch(CONTACTS_CREATE_ROLE, {
                    selectedResources: params.selectedResources,
                });
                const customer: Customer = state.customer!;
                const contact = transformContactToUpdateAndCreateUser(
                    params.contact,
                    customer,
                    state.contactView.statuses,
                    state.roleCreated,
                );

                const ticket: TicketRequest = {
                    description: `El contacto ${params.userContact.name} ${params.userContact.lastName} del cliente ${params.userContact.customer.name} creó el usuario ${params.contact.name} ${params.contact.lastName}, ${params.contact.email} para el cliente ${customer.name} en el Portal de Clientes Daycohost. `,
                    ticketType: {
                        id: Tickets.REQUIREMENT.id,
                        name: TicketType.CONTACT,
                    },
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: {
                            id: customer.category ? customer.category!.id : -1,
                        },
                        contact: {
                            id: params.userContact.id,
                            name: params.userContact.name,
                            lastName: params.userContact.lastName,
                            identification: params.userContact.ci!,
                            phone: params.userContact.phone!,
                            email: params.userContact.email,
                        },
                    },
                    contact: contact,
                };

                await contactsRepository.updateContact(JSON.parse(JSON.stringify(ticket)));

                return {
                    status: ResultStatus.SUCCESS,
                    message: `El usuario ${params.contact.name} ${params.contact.lastName} ha sido creado`,
                };
            } catch (e) {
                return {
                    status: ResultStatus.ERROR,
                    message: 'Ha ocurrido un error inesperado, vuelva a intentarlo en otro momento',
                };
            }
        },

        async [ContactsActions.DELETE_CONTACT](
            {},
            params: {
                contact: ContactSelection;
                customer: Customer;
                userContact: Contact;
                isDaycoUser: boolean;
            },
        ): Promise<RequestResponse> {
            try {
                const contact: DeleteContactRequest = {
                    contactManagementType: ContactManagementType.DELETE,
                    contactId: params.contact.id,
                    name: params.contact.name,
                    lastName: params.contact.lastName,
                    ci: params.contact.ci,
                    position: params.contact.position,
                    email: params.contact.email,
                    phone: params.contact.phone,
                    cellphone: params.contact.cellphone,
                    customerId: params.customer.id,
                    customerName: params.customer.name,
                    contactType: {
                        id: params.contact.contactTypeId!,
                        name: params.contact.contactTypeName!,
                    },
                    scalabilityLevel: {
                        id: params.contact.scalabilityLevelId!,
                        name: params.contact.scalabilityLevelName!,
                    },
                };
                const ticket: TicketRequest = {
                    description: `El contacto ${params.userContact.name} ${params.userContact.lastName} del cliente ${params.userContact.customer.name} eliminó el contacto ${params.contact.name} ${params.contact.lastName}, ${params.contact.email} para el cliente ${params.customer.name} en el Portal de Clientes Daycohost`,
                    ticketType: {
                        id: Tickets.REQUIREMENT.id,
                        name: TicketType.CONTACT,
                    },
                    customer: {
                        id: params.customer.id,
                        name: params.customer.name,
                        category: {
                            id: params.customer.category ? params.customer.category.id : -1,
                        },
                        contact: {
                            id: params.isDaycoUser ? undefined! : params.userContact.id!,
                            name: params.userContact.name,
                            lastName: params.userContact.lastName,
                            identification: params.isDaycoUser
                                ? params.contact.ci
                                : params.userContact.ci!,
                            phone: params.isDaycoUser
                                ? params.contact.phone
                                : params.userContact.phone!,
                            email: params.isDaycoUser
                                ? params.contact.email
                                : params.userContact.email,
                        },
                    },
                    contact,
                };

                await contactsRepository.deleteContact(ticket);

                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ContactsActions.FETCH_DIRECTORY](
            { commit },
            customerId: number,
        ): Promise<RequestResponse> {
            try {
                const contacts = await customerRepository.getDirectoryContacts(customerId);

                commit(ContactsMutations.SET_DIRECTORY, contacts);

                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ContactsActions.FETCH_USER_DATA_OF_CONTACT](
            { commit },
            contactId: number,
        ): Promise<RequestResponse> {
            try {
                const contactHistory = await ticketsRepository.getTicketContactHistory(contactId);
                commit(ContactsMutations.SET_SELECTED_CONTACT_USER_DATA, contactHistory);

                const contacts = (await contactsRepository.getContact(contactId)).data;

                commit(
                    ContactsMutations.SET_SELECTED_CONTACT_PRIVILEGES,
                    contacts.contactPrivileges,
                );

                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ContactsActions.SELECT_CONTACT_IN_DIRECTORY](
            { commit, dispatch, state },
            contactId: number,
        ): Promise<RequestResponse> {
            try {
                const selectedContact = state.directoryContacts.find(
                    contact => contact.id === contactId,
                );

                commit(ContactsMutations.SET_SELECTED_CONTACT_DETAILS, selectedContact);

                return dispatch(ContactsActions.FETCH_USER_DATA_OF_CONTACT, contactId);
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default contacts;
